import { FilePenLine } from "lucide-react";
import { readPortalWork } from "@/features/advice-portal/lib/portal-work";
import type { Access } from "@/shared/api/grants";
import { ADVICE_PORTAL_NEW, DOCK_PATHS, isRecordPaneRoute } from "@/shared/ui/dock-pages";
import {
  AdminIcon,
  ClientsIcon,
  DocumentsIcon,
  FaIcon,
  HomeIcon,
  OrdersIcon,
  QuantIcon,
  type ShellIcon,
} from "@/shared/ui/layout/shell-icons";

export interface DockEntry {
  kind: "entry";
  key: string;
  path: string;
  labelKey: string;
  icon: ShellIcon;
  matches: (pathname: string) => boolean;
  allowed?: (access: Access) => boolean;
  resume?: () => string | null;
  tour?: string;
}

interface DockSeparator {
  kind: "separator";
  key: string;
}

export type DockItem = DockEntry | DockSeparator;

export function visibleDockItems(
  access: Access | null,
  items: readonly DockItem[] = DOCK,
): DockItem[] {
  const allowed = items.filter(
    (item) =>
      item.kind === "separator" || !item.allowed || (access !== null && item.allowed(access)),
  );
  return allowed.filter(
    (item, index, all) =>
      item.kind === "entry" ||
      (index > 0 && index < all.length - 1 && all[index - 1].kind === "entry"),
  );
}

export function dockEntryAllowed(
  key: string,
  access: Access,
  items: readonly DockItem[] = DOCK,
): boolean {
  const entry = items.find((item) => item.kind === "entry" && item.key === key);
  return entry?.kind === "entry" && (!entry.allowed || entry.allowed(access));
}

export function activeDockEntry(
  pathname: string,
  items: readonly DockItem[] = DOCK,
): DockEntry | null {
  return (
    items.find((item): item is DockEntry => item.kind === "entry" && item.matches(pathname)) ?? null
  );
}

function under(root: string): (pathname: string) => boolean {
  return (pathname) => pathname === root || pathname.startsWith(`${root}/`);
}

function exactly(path: string): (pathname: string) => boolean {
  return (pathname) => pathname === path;
}

export const DOCK: readonly DockItem[] = [
  {
    kind: "entry",
    key: "home",
    path: DOCK_PATHS.home,
    labelKey: "nav.home",
    icon: HomeIcon,
    matches: exactly(DOCK_PATHS.home),
  },
  {
    kind: "entry",
    key: "clients",
    path: DOCK_PATHS.clients,
    labelKey: "nav.clients",
    icon: ClientsIcon,
    matches: under(DOCK_PATHS.clients),
    tour: "clients",
  },
  {
    kind: "entry",
    key: "documents",
    path: DOCK_PATHS.advicePortal,
    labelKey: "nav.documents",
    icon: DocumentsIcon,
    matches: exactly(DOCK_PATHS.advicePortal),
    tour: "docs",
  },
  {
    kind: "entry",
    key: "advicePortal",
    path: ADVICE_PORTAL_NEW,
    labelKey: "nav.advicePortal",
    icon: FilePenLine,
    matches: isRecordPaneRoute,
    resume: () => readPortalWork()?.path ?? null,
    tour: "portal",
  },
  {
    kind: "entry",
    key: "orders",
    path: DOCK_PATHS.orderInstructions,
    labelKey: "nav.orders",
    icon: OrdersIcon,
    matches: under(DOCK_PATHS.orderInstructions),
    tour: "orders",
  },
  { kind: "separator", key: "admin-tools" },
  {
    kind: "entry",
    key: "quantAdmin",
    path: DOCK_PATHS.quantAdmin,
    labelKey: "nav.quantAdmin",
    icon: QuantIcon,
    matches: under(DOCK_PATHS.quantAdmin),
    tour: "quant-admin",
    allowed: (access) => access.quantAdmin,
  },
  {
    kind: "entry",
    key: "admin",
    path: DOCK_PATHS.admin,
    labelKey: "nav.admin",
    icon: AdminIcon,
    matches: under(DOCK_PATHS.admin),
    tour: "admin",
    allowed: (access) => access.userAdmin,
  },
  {
    kind: "entry",
    key: "fa",
    path: DOCK_PATHS.fa,
    labelKey: "nav.fa",
    icon: FaIcon,
    matches: under(DOCK_PATHS.fa),
    tour: "fa",
    allowed: (access) => access.superuser && access.flags.fa === true,
  },
];
